import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { AboutHero } from "@/components/site/about/AboutHero";
import { AboutWelcome } from "@/components/site/about/AboutWelcome";
import { PropertyFacts } from "@/components/site/about/PropertyFacts";
import { AboutAccommodations } from "@/components/site/about/AboutAccommodations";
import { WhoWeWelcome } from "@/components/site/about/WhoWeWelcome";
import { AboutCommunity } from "@/components/site/about/AboutCommunity";
import { AboutLocation } from "@/components/site/about/AboutLocation";
import { AboutCTA } from "@/components/site/about/AboutCTA";
import { ABOUT_PATH, BUSINESS, CAMPGROUND_ID, FACEBOOK_URL } from "@/lib/site";
import { PHOTOS } from "@/components/site/images";

const TITLE = "About Amigo Inn & RV Park | Zapata, Texas";
const DESCRIPTION =
  "Learn about Amigo Inn & RV Park in Zapata, TX, a year-round campground near Falcon Lake with 65 RV sites, rooms, gathering spaces, and warm hospitality.";
const CANONICAL = `https://amigoinn-rvpark.com${ABOUT_PATH}`;
const heroImage = PHOTOS.cabinRow;
const OG_IMAGE = `https://amigoinn-rvpark.com${heroImage.src}`;

export const Route = createFileRoute("/about-us")({
  component: AboutUsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "preload",
        as: "image",
        href: heroImage.src,
        imageSrcSet: heroImage.srcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: TITLE,
          url: CANONICAL,
          description: DESCRIPTION,
          image: OG_IMAGE,
          isPartOf: {
            "@type": "WebSite",
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
          },
          about: { "@id": CAMPGROUND_ID },
          mainEntity: {
            "@id": CAMPGROUND_ID,
            "@type": ["Campground", "LodgingBusiness"],
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
            telephone: BUSINESS.phone,
            email: BUSINESS.email,
            sameAs: [FACEBOOK_URL],
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.street,
              addressLocality: BUSINESS.city,
              addressRegion: BUSINESS.region,
              postalCode: BUSINESS.postalCode,
              addressCountry: BUSINESS.country,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: BUSINESS.siteUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "About Us",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function AboutUsPage() {
  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        <AboutHero />
        <AboutWelcome />
        <PropertyFacts />
        <AboutAccommodations />
        <WhoWeWelcome />
        <AboutCommunity />
        <AboutLocation />
        <AboutCTA />
      </main>

      <SiteFooter />
      <MobileBookingBar />
    </div>
  );
}
